/*
  Vídeo do produto na galeria da PDP: autoplay mudo em loop quando entra na
  tela, toque/clique pausa e retoma. Markup, CSS e a lista de versões do mp4
  vêm de snippets/ws-product-video.liquid; este arquivo só monta a camada
  por cima do pôster do Dawn (snippets/product-thumbnail.liquid, core, não
  editado) e cuida de quando tocar.

  Mesma base do vídeo do hero (sections/ws-editorial-hero.liquid,
  ws-hero-video): sem autoplay pra quem pede menos movimento ou está em
  economia de dados/rede lenta, src só definido depois do load da página e
  camada visível só no evento `playing`.
*/
(function () {
  if (customElements.get('ws-product-video')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection;
  var lowData = !!conn && (conn.saveData === true || ['slow-2g', '2g', '3g'].indexOf(conn.effectiveType) !== -1);
  // Sem autoplay, a camada continua: o primeiro toque toca o vídeo ali mesmo,
  // em vez dos dois toques do Dawn no mobile.
  var AUTOPLAY = !reduceMotion && !lowData;

  // Modais e drawers do Dawn travam o scroll com overflow-hidden* no body
  // (ModalDialog, cart drawer, menu drawer). Com um deles aberto o vídeo
  // fica parado.
  var OVERLAY_OPEN = /(^|\s)overflow-hidden/;

  var instances = new Set();
  var pageLoaded = document.readyState === 'complete';

  // Largura mínima do arquivo: 480 no mobile (480x720) e no desktop comum,
  // 720 no desktop retina (720x1080). Decisão de 28/09/2026: o slide mobile
  // tem ~360px CSS e a célula da grade desktop ~420px. Em pixels de largura,
  // e não pelo nome da versão, pra valer também pra vídeo horizontal.
  function pickSource(media) {
    var mp4 = (media.sources || [])
      .filter(function (source) {
        return source.format === 'mp4' && source.url;
      })
      .sort(function (a, b) {
        return a.width - b.width;
      });
    if (!mp4.length) return null;
    var desktop = window.matchMedia('(min-width: 750px)').matches;
    var target = desktop && window.devicePixelRatio >= 1.5 ? 720 : 480;
    var pick =
      mp4.find(function (source) {
        return source.width >= target;
      }) || mp4[mp4.length - 1];
    return pick.url;
  }

  class WsProductVideo extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      // Reconecta quando o Dawn reordena a galeria numa troca de variante
      // (product-info.js move os <li>, não recria): observa de novo, sem
      // remontar nada.
      if (pageLoaded) this.observe();
    }

    disconnectedCallback() {
      instances.delete(this);
      if (this.visibility) this.visibility.disconnect();
      this.visibility = null;
    }

    setup(media, toggle) {
      this.media = media;
      this.inView = false;
      this.userPaused = false;
      this.userStarted = false;
      this.classList.add('is-paused');

      var video = document.createElement('video');
      video.className = 'ws-product-video__video';
      // muted no atributo E na propriedade: o Safari ignora o atributo quando o
      // src é definido por JS, e vídeo com som nunca ganha autoplay.
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      ['muted', 'loop', 'playsinline', 'disablepictureinpicture'].forEach(function (attribute) {
        video.setAttribute(attribute, '');
      });
      // O botão é quem fala com o leitor de tela.
      video.setAttribute('aria-hidden', 'true');
      this.video = video;
      this.toggle = toggle;
      this.syncLabel();

      var self = this;
      video.addEventListener('playing', function () {
        self.classList.add('is-revealed');
        self.syncLabel();
      });
      video.addEventListener('play', this.syncLabel.bind(this));
      video.addEventListener('pause', function () {
        self.syncLabel();
        // window.pauseAllMedia() do Dawn pausa todo <video> da página ao abrir
        // E ao fechar um modal, este último depois de já ter liberado o body.
        // Reavaliar no tick seguinte retoma o vídeo quando nada mais o impede.
        setTimeout(self.update.bind(self), 0);
      });
      video.addEventListener('error', this.fallback.bind(this));
      toggle.addEventListener('click', this.onToggle.bind(this));

      this.append(video, toggle);
    }

    observe() {
      if (this.visibility || !this.video) return;
      var self = this;
      // Metade visível: no desktop o vídeo fica na 2ª ou 3ª linha da grade e
      // não começa com a pontinha aparecendo na dobra; no mobile, só quando
      // a cliente desliza até o slide dele.
      this.visibility = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.inView = entry.intersectionRatio >= 0.5;
          });
          self.update();
        },
        { threshold: [0, 0.5] }
      );
      this.visibility.observe(this);
    }

    shouldPlay() {
      return (
        this.inView &&
        !this.userPaused &&
        (AUTOPLAY || this.userStarted) &&
        !OVERLAY_OPEN.test(document.body.className)
      );
    }

    update() {
      if (!this.video) return;
      if (this.shouldPlay()) {
        this.play();
      } else if (!this.video.paused) {
        this.video.pause();
      }
    }

    play() {
      if (!this.video.getAttribute('src')) {
        var src = pickSource(this.media);
        if (!src) return this.fallback();
        this.video.src = src;
      }
      var attempt = this.video.play();
      // Autoplay recusado: o pôster continua à vista com o ícone de play, e o
      // toque seguinte (gesto da cliente) toca normalmente.
      if (attempt) attempt.catch(function () {});
    }

    onToggle(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.video.paused) {
        this.userPaused = false;
        this.userStarted = true;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    syncLabel() {
      if (!this.video || !this.toggle) return;
      var paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    }

    // Arquivo que não carrega: sai a camada e volta o vídeo do Dawn como era.
    fallback() {
      var container = this.parentElement;
      if (container) {
        container.querySelectorAll('[inert][data-ws-product-video-inert]').forEach(function (element) {
          element.removeAttribute('inert');
          element.removeAttribute('data-ws-product-video-inert');
        });
      }
      this.remove();
    }
  }

  customElements.define('ws-product-video', WsProductVideo);

  function mount(data) {
    var sectionId = data.getAttribute('data-ws-product-video');
    var gallery = document.getElementById('MediaGallery-' + sectionId);
    var template = document.getElementById('WsProductVideoToggle-' + sectionId);
    if (!gallery || !template) return;
    // O quick add injeta o HTML inteiro da PDP numa gaveta (quick-add.js
    // reescreve os ids e reexecuta os scripts), e com ela aberta o body fica
    // travado: o vídeo nunca tocaria. Lá fica o comportamento do Dawn.
    if (gallery.closest('quick-add-modal')) return;

    var medias;
    try {
      medias = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    medias.forEach(function (media) {
      var item = gallery.querySelector('li[data-media-id="' + sectionId + '-' + media.id + '"]');
      var container = item && item.querySelector('.product-media-container');
      if (!container || container.querySelector('ws-product-video')) return;

      // O pôster clicável do Dawn sai da ordem de foco e do clique: o toque vai
      // pro nosso botão, sem abrir o modal nem baixar o mp4 grande.
      container.querySelectorAll(':scope > modal-opener, :scope > deferred-media').forEach(function (element) {
        element.setAttribute('inert', '');
        element.setAttribute('data-ws-product-video-inert', '');
      });

      var layer = document.createElement('ws-product-video');
      layer.setup(media, template.content.firstElementChild.cloneNode(true));
      // Antes do badge da PDP, que precisa ficar por cima do vídeo.
      container.insertBefore(layer, container.querySelector(':scope > .ws-pdp-badge'));
    });
  }

  document.querySelectorAll('script[data-ws-product-video]').forEach(mount);

  // Só começa a observar (e portanto a baixar) depois do load: antes disso o
  // vídeo disputaria banda com a primeira foto da galeria, que é o LCP da PDP.
  function start() {
    pageLoaded = true;
    instances.forEach(function (instance) {
      instance.observe();
    });
  }
  if (pageLoaded) start();
  else window.addEventListener('load', start, { once: true });

  new MutationObserver(function () {
    instances.forEach(function (instance) {
      instance.update();
    });
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
